/**
 * Widget Picker - Sheet component to toggle widget visibility.
 * Shows all available widgets with toggle switches.
 */

import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Switch } from '@/components/ui/switch';
import { LayoutGrid } from 'lucide-react';
import { WIDGET_REGISTRY, getAllWidgets } from './widgetRegistry';
import * as LucideIcons from 'lucide-react';
import { LucideIcon } from 'lucide-react';

interface WidgetPickerProps {
  hiddenWidgets: string[];
  onToggleWidget: (widgetId: string, visible: boolean) => void;
}

export function WidgetPicker({ hiddenWidgets, onToggleWidget }: WidgetPickerProps) {
  const widgets = getAllWidgets();

  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm">
          <LayoutGrid className="h-4 w-4" />
          Widgets
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Dashboard Widgets</SheetTitle>
          <SheetDescription>
            Toggle widgets to show or hide them on your dashboard
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-3">
          {widgets.map(widget => {
            const isVisible = !hiddenWidgets.includes(widget.id);
            // Get icon component dynamically
            const IconComponent = (LucideIcons as Record<string, LucideIcon>)[widget.icon] || LucideIcons.Box;

            return (
              <div
                key={widget.id}
                className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-primary/10 rounded-md">
                    <IconComponent className="h-4 w-4 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-sm">{widget.name}</p>
                    <p className="text-xs text-muted-foreground line-clamp-1">
                      {widget.description}
                    </p>
                  </div>
                </div>
                <Switch
                  checked={isVisible}
                  onCheckedChange={checked => onToggleWidget(widget.id, checked)}
                  aria-label={`Toggle ${widget.name}`}
                />
              </div>
            );
          })}
        </div>
      </SheetContent>
    </Sheet>
  );
}
